import Alpine from 'alpinejs';
import api, { requireAuth } from './api.js';

function createInkoCreationEditor() {
  return {
    projectId: '',
    loading: true,
    busy: false,
    editor: null,
    selectedId: null,
    textDraft: '',
    regenPrompt: '',
    versions: [],
    showVersions: false,

    init() {
      this.projectId = this.$el.dataset.projectId || '';
      if (!requireAuth() || !this.projectId) return;
      this.load();
    },

    async load() {
      this.loading = true;
      try {
        this.editor = await api.getEditor(this.projectId);
        const sel = this.editor.canvas_state?.selected_layer_id;
        this.selectedId = sel || this.editor.layers?.[0]?.id || null;
        this.syncTextDraft();
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal memuat editor.', 'error');
      } finally {
        this.loading = false;
      }
    },

    get selectedLayer() {
      return (this.editor?.layers || []).find(l => l.id === this.selectedId) || null;
    },

    get aspectRatio() {
      const c = this.editor?.canvas || {};
      const w = Number(c.width_cm || 3);
      const h = Number(c.height_cm || 1);
      return w / Math.max(h, 0.1);
    },

    selectLayer(id) {
      this.selectedId = id;
      this.syncTextDraft();
    },

    syncTextDraft() {
      const layer = this.selectedLayer;
      this.textDraft = layer?.content?.text != null ? String(layer.content.text) : '';
    },

    layerStyle(layer) {
      const t = layer.transform || {};
      return {
        left: `${t.x_pct ?? 0}%`,
        top: `${t.y_pct ?? 0}%`,
        width: `${t.w_pct ?? 10}%`,
        height: `${t.h_pct ?? 10}%`,
        opacity: layer.visible === false ? 0.3 : Number(layer.style?.opacity ?? 1),
      };
    },

    layerClass(layer) {
      let c = 'inko-layer';
      if (layer.id === this.selectedId) c += ' is-selected';
      if (layer.type === 'background') c += ' is-bg';
      return c;
    },

    async patchLayer(payload) {
      if (!this.selectedId) return;
      this.busy = true;
      try {
        this.editor = await api.updateLayer(this.projectId, this.selectedId, payload);
        window.toast?.('Layer diperbarui.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Update gagal.', 'error');
      } finally {
        this.busy = false;
      }
    },

    nudge(field, delta) {
      const layer = this.selectedLayer;
      if (!layer) return;
      const t = { ...(layer.transform || {}) };
      const key = field === 'x' ? 'x_pct' : field === 'y' ? 'y_pct' : field === 'w' ? 'w_pct' : 'h_pct';
      t[key] = Math.max(0, Math.min(100, Number(t[key] ?? 0) + delta));
      this.patchLayer({ transform: t });
    },

    async saveText() {
      await this.patchLayer({ content: { text: this.textDraft } });
    },

    async duplicateLayer() {
      if (!this.selectedId) return;
      this.busy = true;
      try {
        this.editor = await api.duplicateLayer(this.projectId, this.selectedId);
        window.toast?.('Layer diduplikasi.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Duplikasi gagal.', 'error');
      } finally {
        this.busy = false;
      }
    },

    async deleteLayer() {
      if (!this.selectedId || !confirm('Hapus layer ini?')) return;
      this.busy = true;
      try {
        this.editor = await api.deleteLayer(this.projectId, this.selectedId);
        this.selectedId = this.editor.layers?.[0]?.id || null;
        window.toast?.('Layer dihapus.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Hapus gagal.', 'error');
      } finally {
        this.busy = false;
      }
    },

    async regenerateLayer() {
      if (!this.selectedId) return;
      this.busy = true;
      try {
        await api.regenerateLayer(this.projectId, this.selectedId, this.regenPrompt || undefined);
        window.toast?.('Regenerate dimulai.', 'success');
        setTimeout(() => this.load(), 2000);
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Regenerate gagal.', 'error');
      } finally {
        this.busy = false;
      }
    },

    async loadVersions() {
      this.showVersions = true;
      try {
        this.versions = await api.listVersions(this.projectId);
      } catch {
        this.versions = [];
      }
    },

    async restoreVersion(versionId) {
      if (!confirm('Pulihkan versi ini?')) return;
      this.busy = true;
      try {
        this.editor = await api.restoreVersion(this.projectId, versionId);
        this.showVersions = false;
        window.toast?.('Versi dipulihkan.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Restore gagal.', 'error');
      } finally {
        this.busy = false;
      }
    },
  };
}

Alpine.data('inkoCreationEditor', createInkoCreationEditor);
